import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { dict } from "@/lib/i18n";
import { langFromSearch, isLangNeutralPath, withLang } from "@/lib/langUrl";

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    // Public pages: the URL decides (?lang=ar → ar, otherwise en). The saved
    // preference is only used on language-neutral routes (/admin).
    try {
      if (!isLangNeutralPath(window.location.pathname)) return langFromSearch(window.location.search);
      return localStorage.getItem("propertic_lang") || "en";
    } catch { return "en"; }
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("propertic_lang", lang);
    // Trigger the digit-morph flourish on every `.tabular` number in the DOM
    // whenever the language flips. Class is removed after 700ms so it can
    // fire again on the next toggle.
    document.body.classList.add("lang-morphing");
    const id = setTimeout(() => document.body.classList.remove("lang-morphing"), 700);
    return () => clearTimeout(id);
  }, [lang]);

  const toggle = useCallback(() => setLang((l) => (l === "en" ? "ar" : "en")), []);

  const lh = useCallback((to) => withLang(to, lang), [lang]);

  const t = useCallback((path) => {
    const parts = path.split(".");
    let node = dict[lang];
    for (const p of parts) {
      if (node == null) return path;
      node = node[p];
    }
    return node ?? path;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, lh, isRTL: lang === "ar" }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Keeps language in step with the URL on every public navigation (back/forward included).
export const LangUrlSync = () => {
  const { setLang } = useLang();
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!isLangNeutralPath(pathname)) setLang(langFromSearch(search));
  }, [pathname, search, setLang]);
  return null;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
};
